import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CityProvider } from "@/context/CityContext";
import { LiveProvider } from "@/context/LiveContext";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import DartTransition from "@/components/DartTransition";
import InstallPrompt from "@/components/InstallPrompt";
import WhatsAppButton from "@/components/WhatsAppButton";
import MacToaster from "@/components/MacToaster";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useRipple } from "@/lib/useRipple";
import Home from "@/pages/Home";
import Properties from "@/pages/Properties";
import PropertyDetail from "@/pages/PropertyDetail";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import OwnerDashboard from "@/pages/OwnerDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import CustomerAccount from "@/pages/CustomerAccount";
import Contact from "@/pages/Contact";
import Blogs, { BlogDetail } from "@/pages/Blogs";
import Pricing from "@/pages/Pricing";
import Highlighted from "@/pages/Highlighted";
import Status from "@/pages/Status";

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><div className="h-8 w-8 rounded-full border-2 border-[color:var(--maroon)] border-t-transparent animate-spin"/></div>;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname }}/>;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace/>;
  return children;
}

function PageShell({ children }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3, delay: 0.15 }}>
      {children}
    </motion.div>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

const DASH = ["/owner", "/admin", "/account"];

function AppRoutes() {
  const loc = useLocation();
  const page = (el) => <ErrorBoundary inline resetKey={loc.pathname}><PageShell key={loc.pathname}>{el}</PageShell></ErrorBoundary>;
  return (
    <Routes>
      <Route path="/" element={page(<Home/>)}/>
      <Route path="/properties" element={page(<Properties/>)}/>
      <Route path="/property/:id" element={page(<PropertyDetail/>)}/>
      <Route path="/highlighted" element={page(<Highlighted/>)}/>
      <Route path="/blogs" element={page(<Blogs/>)}/>
      <Route path="/blogs/:slug" element={page(<BlogDetail/>)}/>
      <Route path="/pricing" element={page(<Pricing/>)}/>
      <Route path="/contact" element={page(<Contact/>)}/>
      <Route path="/login" element={page(<Login/>)}/>
      <Route path="/register" element={page(<Register/>)}/>
      <Route path="/forgot-password" element={page(<ForgotPassword/>)}/>
      <Route path="/reset-password" element={page(<ResetPassword/>)}/>
      <Route path="/owner" element={<Protected roles={["venue_owner", "staff", "super_admin", "major_admin", "admin"]}>{page(<OwnerDashboard/>)}</Protected>}/>
      <Route path="/admin" element={<Protected roles={["super_admin", "major_admin", "admin"]}>{page(<AdminDashboard/>)}</Protected>}/>
      <Route path="/account" element={<Protected roles={["customer", "staff", "venue_owner", "super_admin", "major_admin", "admin"]}>{page(<CustomerAccount/>)}</Protected>}/>
      <Route path="/status" element={page(<Status/>)}/>
      <Route path="*" element={page(<NotFound/>)}/>
    </Routes>
  );
}

function NotFound() {
  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6 py-20">
      <div className="font-display text-6xl text-[color:var(--maroon)]">Missed the board</div>
      <p className="text-[color:var(--ink-2)] mt-3 max-w-md">This page doesn't exist. Head back home or browse every listing.</p>
      <div className="flex gap-3 mt-6"><a href="/" className="btn-wine">Go to home</a><a href="/properties" className="btn-ghost">Browse listings</a></div>
    </div>
  );
}

function Chrome() {
  useRipple();
  const { pathname } = useLocation();
  const dash = DASH.some((p) => pathname.startsWith(p));
  return (
    <>
      <ScrollToTop/>
      <DartTransition/>
      <Navbar/>
      <main>
        <AppRoutes/>
      </main>
      {!dash && <Footer/>}
      <InstallPrompt/>
      <WhatsAppButton/>
      <MacToaster/>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CityProvider>
            <LiveProvider>
              <Chrome/>
            </LiveProvider>
          </CityProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
